import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { fetchListingsOverview } from "@/lib/super-admin";
import { SERVICE_LABEL, SERVICE_TYPES, type ServiceType } from "@/lib/services";
import { LISTING_STATUS_CLASS, LISTING_STATUS_LABEL, type ListingStatus } from "@/lib/listings";

export const Route = createFileRoute("/_authenticated/super-admin/listings")({
  head: () => ({
    meta: [
      { title: "Listings · Landoo Super Admin" },
      {
        name: "description",
        content: "Platform-wide listing overview across Sale, Rent, Lease and Parking.",
      },
    ],
  }),
  component: ListingsPage,
});

const SERVICE_CHIP: Record<ServiceType, string> = {
  sale: "bg-sale/15 text-sale",
  rent: "bg-rent/15 text-rent",
  lease: "bg-lease/15 text-lease",
  parking: "bg-parking/15 text-parking",
};

function ListingsPage() {
  const { data = [], isLoading } = useQuery({
    queryKey: ["super-admin", "listings-overview"],
    queryFn: () => fetchListingsOverview(100),
  });
  const [service, setService] = useState<ServiceType | "all">("all");
  const [status, setStatus] = useState<ListingStatus | "all">("all");

  const rows = useMemo(
    () =>
      data.filter(
        (r) =>
          (service === "all" || r.service === service) && (status === "all" || r.status === status),
      ),
    [data, service, status],
  );

  return (
    <DashboardShell title="Listings" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {(["all", ...SERVICE_TYPES] as const).map((s) => (
          <button
            key={s}
            onClick={() => setService(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              service === s ? "bg-brown text-primary-foreground" : "bg-secondary text-brown-dark"
            }`}
          >
            {s === "all" ? "All" : SERVICE_LABEL[s]}
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {(["all", "draft", "pending", "published", "rejected", "archived"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              status === s
                ? "bg-brown-dark text-primary-foreground"
                : "border border-border bg-card text-brown-dark"
            }`}
          >
            {s === "all" ? "All statuses" : LISTING_STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading listings…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No listings match.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={`${r.service}-${r.id}`}
              className="rounded-2xl border border-border bg-card p-4 shadow-soft"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SERVICE_CHIP[r.service]}`}
                >
                  {SERVICE_LABEL[r.service]}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    LISTING_STATUS_CLASS[r.status as ListingStatus] ??
                    "bg-secondary text-brown-dark"
                  }`}
                >
                  {LISTING_STATUS_LABEL[r.status as ListingStatus] ?? r.status}
                </span>
              </div>
              <h3 className="mt-1 truncate text-sm font-semibold text-brown-dark">{r.title}</h3>
              <p className="text-xs text-muted-foreground">{r.city ?? "—"}</p>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
